import React, { useCallback, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import {
  HISTORICAL_PREFILL_ERROR_TYPE,
  HISTORICAL_PREFILL_MESSAGE,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/historicalPrefill/constants.ts';
import type { THistoricalPrefillProviderProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/historicalPrefill/types.ts';
import { getHistoricalPrefillFieldName } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/historicalPrefill/utils.ts';

const removeHistoricalPrefillTitle = (element: HTMLElement) => {
  if (element.title === HISTORICAL_PREFILL_MESSAGE) {
    element.removeAttribute('title');
  }
};

export const HistoricalPrefillProvider: React.FC<THistoricalPrefillProviderProps> = ({
  fieldNames = [],
  onPendingFieldsChange,
  children,
}) => {
  const { clearErrors, getFieldState, getValues, setError, setValue } = useFormContext();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const pendingFieldNamesRef = useRef(new Set<string>());
  const fieldElementsRef = useRef(new Map<string, HTMLElement[]>());
  const pendingFieldNamesKeyRef = useRef<string | null>(null);
  const errorClearFrameRef = useRef<number | null>(null);
  const pendingErrorClearFieldNamesRef = useRef(new Set<string>());
  const fieldNamesKey = Array.from(new Set(fieldNames)).join('|');

  const notifyPendingFieldsChange = useCallback(
    (pendingFieldNames: Set<string>) => {
      const nextFieldNames = Array.from(pendingFieldNames);
      const nextFieldNamesKey = nextFieldNames.join('|');

      if (pendingFieldNamesKeyRef.current === nextFieldNamesKey) {
        return;
      }

      pendingFieldNamesKeyRef.current = nextFieldNamesKey;
      onPendingFieldsChange?.(nextFieldNames);
    },
    [onPendingFieldsChange]
  );

  useEffect(() => {
    const nextFieldNames = new Set(fieldNames);
    const fieldElements = new Map<string, HTMLElement[]>();

    fieldElementsRef.current.forEach((elements) => {
      elements.forEach(removeHistoricalPrefillTitle);
    });

    rootRef.current?.querySelectorAll<HTMLElement>('[data-testid]').forEach((element) => {
      const fieldName = getHistoricalPrefillFieldName(element, nextFieldNames);
      if (!fieldName) {
        return;
      }

      const elements = fieldElements.get(fieldName) ?? [];
      elements.push(element);
      fieldElements.set(fieldName, elements);
      element.title = HISTORICAL_PREFILL_MESSAGE;
    });

    pendingFieldNamesRef.current = nextFieldNames;
    fieldElementsRef.current = fieldElements;

    nextFieldNames.forEach((name) => {
      setValue(name, getValues(name), { shouldTouch: true, shouldDirty: false });
      setError(name, {
        type: HISTORICAL_PREFILL_ERROR_TYPE,
      });
    });

    notifyPendingFieldsChange(nextFieldNames);
  }, [fieldNamesKey, getValues, notifyPendingFieldsChange, setError, setValue]);

  useEffect(
    () => () => {
      if (errorClearFrameRef.current !== null) {
        window.cancelAnimationFrame(errorClearFrameRef.current);
      }
    },
    []
  );

  const scheduleHistoricalErrorClear = useCallback(
    (name: string) => {
      pendingErrorClearFieldNamesRef.current.add(name);

      if (errorClearFrameRef.current !== null) {
        return;
      }

      errorClearFrameRef.current = window.requestAnimationFrame(() => {
        pendingErrorClearFieldNamesRef.current.forEach((fieldName) => {
          if (getFieldState(fieldName).error?.type === HISTORICAL_PREFILL_ERROR_TYPE) {
            clearErrors(fieldName);
          }
        });

        pendingErrorClearFieldNamesRef.current.clear();
        errorClearFrameRef.current = null;
      });
    },
    [clearErrors, getFieldState]
  );

  const acknowledge = useCallback(
    (name: string) => {
      const pendingFieldNames = pendingFieldNamesRef.current;
      if (!pendingFieldNames.has(name)) {
        return;
      }

      pendingFieldNames.delete(name);
      fieldElementsRef.current.get(name)?.forEach(removeHistoricalPrefillTitle);
      scheduleHistoricalErrorClear(name);
      notifyPendingFieldsChange(pendingFieldNames);
    },
    [notifyPendingFieldsChange, scheduleHistoricalErrorClear]
  );

  const acknowledgeTarget = useCallback(
    (target: EventTarget | null) => {
      const fieldName = getHistoricalPrefillFieldName(target, pendingFieldNamesRef.current);
      if (fieldName) {
        acknowledge(fieldName);
      }
    },
    [acknowledge]
  );

  return (
    <Box
      ref={rootRef}
      onPointerDownCapture={(event) => acknowledgeTarget(event.target)}
      onFocusCapture={(event) => acknowledgeTarget(event.target)}
    >
      {children}
    </Box>
  );
};
